<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="pa-4">
            <!-- Loading State -->
            <div v-if="loadingData" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="orange"
                size="64"
              ></v-progress-circular>
              <p class="mt-4 text-h6 text-grey">Loading approval details...</p>
            </div>

            <!-- Content (shown when not loading) -->
            <div v-else>
              <!-- Header Section -->
              <div class="mb-4">
                <h2 class="text-h5 font-weight-bold mb-2"> {{ $route.query.page_title  }} Approval Details</h2>
                
                <div class="text-body-2">
                  <div><strong> Code:</strong> {{ documentDetails?.code || 'N/A' }}</div>
                  <div><strong> Date:</strong> {{ formatDateTime(documentDetails?.created_at) }}</div>
                  
                </div>
              </div>

              <div>
                Status: 
                <v-chip :color="action_type === 'approval' ? 'green' : 'blue'">
                  {{ action_type === 'approval' ? 'For Approval' : 'For Review' }}
                </v-chip>

              </div>

            <!-- Show message when all steps are completed -->
            <v-alert
              v-if="approval_process.length > 0 && isProcessComplete"
              type="success"
              icon="mdi-check-all"
              variant="tonal"
              class="mb-4"
              border="start"
              prominent
            >
              <div class="text-subtitle-2 font-weight-bold mb-1">
                ✓ All approval steps completed
              </div>
              <div class="text-body-2">
                <span v-if="wasForwardApproved">
                  This request has been fully approved. The approval process was completed early.
                </span>
                <span v-else>
                  This request has been fully processed.
                </span>
              </div>
            </v-alert>

            <!-- Approval Workflow Timeline -->
            <div class="mb-4">
              <div class="horizontal-timeline">
                <div 
                  v-for="(approval, index) in approval_process" 
                  :key="index"
                  class="timeline-step"
                >
                  <div 
                    v-if="index > 0" 
                    class="step-connector"
                    :class="{ 
                      'connector-approved': approval_process[index - 1]?.status?.toLowerCase() === 'approved' || (isProcessComplete && (approval_process[index - 1]?.status?.toLowerCase() === 'approved' || approval_process[index - 1]?.status?.toLowerCase() === 'pending')),
                      'connector-returned': approval_process[index - 1]?.status?.toLowerCase() === 'returned',
                    }"
                  >
                    <div class="connector-arrow"></div>
                  </div>
                  <div class="step-content">
                    <div 
                      class="step-circle" 
                      :class="getApprovalClass(approval.status, index)"
                    >
                      <v-icon size="medium" color="white">
                        {{ getApprovalIcon(approval.status, index) }}
                      </v-icon>
                    </div>
                    <div class="step-info">
                      <div class="step-title status-approved-text" v-if="approval.status === 'Forwarded'">
                        Forward By {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-title status-approved-text" v-if="approval.status === 'Approved'">
                        Approved By {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-title status-cancelled-text" v-else-if="approval.status === 'Returned'">
                        Returned By {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-title status-cancelled-text" v-else-if="approval.status === 'Cancelled'">
                        Cancelled By {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-title status-skipped-text" v-else-if="approval.status === 'Pending' && isProcessComplete">
                        Skipped - {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-title" v-else-if="approval.status === 'Pending' && !isProcessComplete && isCurrentStep(index)">
                        <span class="current-step-title">Waiting for {{ approval.employee?.branch.name || 'Approver' }}</span>
                      </div>
                      <div class="step-title status-pending-text" v-else-if="approval.status === 'Pending' && !isProcessComplete">
                        Waiting for {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-title" v-else>
                        {{ approval.employee?.branch.name || 'Approver' }}
                      </div>
                      <div class="step-status">
                        <span v-if="approval.status === 'Approved'">
                          <div v-if="approval.employee?.name" class="employee-name">
                            {{ approval.employee.name }}
                          </div>
                          
                          {{ formatDateTime(approval.approved_at) }}
                        </span>
                        <span v-else-if="approval.status === 'Cancelled'">
                          
                          {{ formatDateTime(approval.approved_at) }}
                        </span>
                        <span v-else-if="approval.status === 'Pending' && isProcessComplete">
                          
                          <div class="text-grey" style="font-style: italic;">
                            Process completed early
                          </div>
                        </span>
                        <span v-else-if="approval.status === 'Pending' && !isProcessComplete">
                          
                          <div v-if="approval.employee?.name" class="employee-name">
                            {{ approval.employee.name }}
                          </div>
                          <div v-if="isCurrentStep(index)">
                            Started: {{ formatDateTime(getCurrentStepStartTime(index)) }}
                          </div>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">Previous Comments:</h3>
              <div v-if="previousComments.length > 0">
                <div 
                  v-for="(comment, index) in previousComments" 
                  :key="index"
                  class="comment-box mb-2"
                  :class="{
                    'comment-box-rejected': comment.status === 'Cancelled',
                    'comment-box-approved': comment.status === 'Approved'
                  }"
                >
                  <div class="comment-header">
                    <span class="comment-status" :class="{
                      'status-rejected': comment.status === 'Cancelled',
                      'status-approved': comment.status === 'Approved'
                    }">
                      {{ comment.employee?.name }}
                    </span>
                    <span class="comment-status" :class="{
                      'status-rejected': comment.status === 'Cancelled',
                      'status-approved': comment.status === 'Approved'
                    }">
                      {{ comment.status }}
                    </span>
                     {{ comment.approval_step_name?.name }}
                  </div>
                  <div class="comment-body">
                    <span class="comment-status" :class="{
                      'status-rejected': comment.status === 'Cancelled',
                      'status-approved': comment.status === 'Approved'
                    }">
                      Comments: 
                    </span> 
                    {{ comment.comments || 'No comments' }}
                  </div>
                  <div class="comment-date">
                    {{ formatDateTime(comment.approved_at) }}
                  </div>
                </div>
              </div>
              <div v-else class="text-grey text-body-2 pa-3" style="background: #f5f5f5; border-radius: 4px;">
                No previous comments yet
              </div>
            </div>

            <div class="mb-4" v-if="reviewerComments.length > 0">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">Reviewer Comments:</h3>
              <div
                v-for="(comment, index) in reviewerComments"
                :key="index"
                class="comment-box mb-2"
              >
                <div class="comment-header">

                  <span class="comment-status" :class="{
                      'status-rejected': comment.status === 'Cancelled',
                      'status-approved': comment.status === 'Approved'
                    }">
                      {{ comment.reviewer_name }}
                    </span>

                  <span class="comment-status">
                    {{ (comment.review_status == 'Not_Accepted' ? 'Not Accepted' : (comment.review_status == 'No_comments' ? 'No Comments' : comment.review_status)) || 'Review' }}
                  </span>
                  <!-- <span v-if="comment.reviewer_code">
                    ({{ comment.reviewer_code }})
                  </span> -->
                  
                </div>
                 <div class="comment-body" style="text-wrap:auto">
                    <span class="comment-status" :class="{
                      'status-rejected': comment.status === 'Cancelled',
                      'status-approved': comment.status === 'Approved'
                    }">
                      Comments: 
                    </span> 
                    {{ comment.comments || 'No comments' }}
                  </div>
                <div class="comment-date" v-if="comment.review_date">
                  {{ formatDateTime(comment.review_date) }}
                </div>
              </div>
            </div>

            <div class="mb-4" v-if="file">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Attach File:</h3>

                <v-row>
                  <v-col cols="12" md="4" v-for="versionFile in file.versions">
                    <v-card
                      style="cursor: pointer;"
                      variant="tonal"
                      class="d-flex align-center pa-3 file-card"
                      rounded="lg"
                      :color="getFileColor(versionFile)"
                      @click="downloadFile(versionFile, 'version_file')"
                    >
                      <v-avatar
                        size="42"
                        class="mr-3"
                        :color="getFileColor(versionFile)"
                        variant="tonal"
                      >
                        <v-icon size="22">
                          {{ getFileIcon(versionFile) }}
                        </v-icon>
                      </v-avatar>

                      <div class="flex-grow-1">
                        <div class="text-body-2 font-weight-medium">
                          <p style="font-size: 12px;">{{ versionFile.original_name }}</p>
                          <p style="font-size: 11px;">Version: {{ versionFile.version_no }}.0</p>
                        </div>
                        <div class="text-caption text-grey">
                          {{ versionFile.extension?.toUpperCase() }}
                        </div>
                      </div>

                      <v-btn
                        icon
                        variant="text"
                        size="small"
                        @click="previewFile(versionFile, 'version_file')"
                        title="Preview"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>

                      <v-btn
                        icon
                        variant="tonal"
                        color="primary"
                        size="small"
                        @click="downloadFile(versionFile, 'version_file')"
                      >
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                    </v-card>
                     <hr>
                    <v-row class="pa-3">
                      <v-col  cols="12" md="10" v-for="supportFile in versionFile.supporting_files">
                        <v-card
                          style="cursor: pointer;"
                          variant="tonal"
                          class="d-flex align-center pa-3 file-card"
                          rounded="lg"
                          :color="getFileColor(supportFile)"
                          @click="downloadFile(supportFile,'support_file')"
                        >
                          <v-avatar
                            size="42"
                            class="mr-3"
                            :color="getFileColor(supportFile)"
                            variant="tonal"
                          >
                            <v-icon size="22">
                              {{ getFileIcon(supportFile) }}
                            </v-icon>
                          </v-avatar>

                          <div class="flex-grow-1">
                            <div class="text-body-2 font-weight-medium">
                              <p style="font-size: 12px;">{{ supportFile.original_name }}</p>
                            </div>
                            <div class="text-caption text-grey">
                              {{ supportFile.extension?.toUpperCase() }}
                            </div>
                          </div>

                          <v-btn
                            icon
                            variant="text"
                            size="small"
                            @click="previewFile(supportFile, 'support_file')"
                            title="Preview"
                          >
                            <v-icon>mdi-eye</v-icon>
                          </v-btn>

                          <v-btn
                            icon
                            variant="tonal"
                            color="primary"
                            size="small"
                            @click="downloadFile(supportFile, 'support_file')"
                          >
                            <v-icon>mdi-download</v-icon>
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

              </div>

            <!-- Confirmation Dialog -->
            <v-dialog v-model="confirmDialog" max-width="400px">
              <v-card>
                <v-card-title class="text-h6 font-weight-bold">
                  {{ confirmMessage }}
                </v-card-title>
                <v-card-text>
                  <p>This action cannot be undone.</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="grey" variant="text" @click="confirmDialog = false">
                    Cancel
                  </v-btn>
                  <v-btn :color="confirmAction === 'approve' ? 'success' : confirmAction === 'forward' ? 'blue' : confirmAction === 'return' ? 'warning' : 'error'" variant="flat" @click="executeConfirmedAction">
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- File Preview Modal -->
            <v-dialog v-model="previewDialog" width="90%" max-width="1000px">
              <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                  <span>{{ currentPreviewFile.name }}</span>
                  <v-btn icon size="small" @click="previewDialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                  <!-- Image Preview -->
                  <div v-if="isImage(currentPreviewFile.mime_type)" class="text-center">
                    <img 
                      :src="fileServerUrl + currentPreviewFile.path" 
                      style="max-width: 100%; max-height: 600px;" 
                      alt="Preview"
                    />
                  </div>
                  <!-- PDF Preview -->
                  <div v-else-if="isPdf(currentPreviewFile.mime_type)">
                    <iframe 
                      :src="fileServerUrl + currentPreviewFile.path" 
                      width="100%" 
                      height="600" 
                      style="border: none;"
                    ></iframe>
                  </div>
                  <!-- Other formats -->
                  <div v-else class="text-center pa-4">
                    <v-icon size="64" class="text-grey-lighten-1">mdi-file</v-icon>
                    <p class="text-subtitle-1 mt-2">Preview not available for this file type</p>
                    <v-btn 
                      variant="outlined" 
                      color="primary"
                      @click="downloadFile(currentPreviewFile, previewFileType)"
                      class="mt-2"
                    >
                      Download to view
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-dialog>

            <!-- Add Your Comment Section -->
            <div class="mb-4" v-if="currentApprovalStep && canApprove">
              <div v-if="hasReviewers && action_type !== 'review'" class="mb-2">
                <h3 class="text-subtitle-2 font-weight-bold mb-2">Select Reviewers:</h3>
                <v-row>
                  <v-col
                    v-for="reviewer in availableReviewers"
                    :key="reviewer.id"
                    cols="12"
                    md="4"
                  >
                    <v-checkbox
                      v-model="selectedReviewerIds"
                      :value="reviewer.employee_id"
                      density="compact"
                      :label="`${reviewer.employee_name} (${reviewer.employee_code})`"
                    />
                  </v-col>
                </v-row>
              </div>

              <h3 v-if="action_type !== 'review'" class="text-subtitle-1 font-weight-bold mb-2">Add Your Comment:</h3>
              <v-textarea
                v-if="action_type !== 'review'"
                v-model="userComment"
                placeholder="Write your comments here (optional)..."
                rows="3"
                variant="outlined"
                bg-color="grey-lighten-5"
                density="compact"
                maxlength="225"
                :counter="225"
                :error-messages="commentError"
                clearable
              ></v-textarea>
              
              <h3 v-if="action_type === 'review'" class="text-subtitle-1 font-weight-bold mb-2">Reviewer Comment:</h3>
              <v-row v-if="action_type === 'review'">
                <v-col cols="12" md="3">
                  <v-select
                    label="Review Status"
                    v-if="action_type === 'review'"
                    v-model="review_status"
                    variant="outlined"
                    density="compact"                
                    :items="[{ label: 'Accepted', value: 'Accepted' }, { label: 'Not Accepted', value: 'Not_Accepted' }, {label: 'No Comments', value: 'No_Comments'}]"
                    :item-title="(item) => `${item.label}`"
                    item-value="value" 
                    hide-details="auto"
                    clearable
                    >
                  </v-select>
                </v-col>

                <v-col cols="12" md="12">
                  <v-textarea 
                    v-if="action_type === 'review'" 
                    v-model="review_comment" 
                    label="Write your review"
                    rows="3"
                    variant="outlined"
                    bg-color="grey-lighten-5"
                    density="compact"
                    maxlength="225"
                    hide-details="auto"
                    clearable
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="2">
                      <div class="flex-grow-1">
                        <v-btn
                          class="bg-brand"
                          variant="flat"
                          size="small"
                          prepend-icon="mdi-upload"
                          @click="triggerNewVersionInput" 
                        >
                          Upload File
                        </v-btn>
                        <span v-if="main_file" class="ml-2 text-caption">
                          {{ main_file.name }}
                        </span>
                        <input
                          ref="newVersionInput"
                          type="file"
                          style="display: none"
                          @change="onNewVersionFileChange"
                        />
                      </div>
                      <v-progress-circular
                        v-if="isUploading"
                        indeterminate
                        size="24"
                        width="2"
                        color="primary"
                      />
                  
                </v-col>

                <v-col v-if="action_type === 'review'" cols="2">
                    <v-btn color="primary" class="bg-brand" variant="flat" size="small" @click="saveReview">
                    Submit
                  </v-btn>
                </v-col>
              </v-row>
                
              <!-- <v-row v-if="action_type === 'review'">
                <v-col cols="3">
                  
                </v-col>
              </v-row> -->
            </div>

            <!-- Action Buttons -->
            <div class="d-flex gap-2" v-if="(canApprove || canReject) && action_type !== 'review'">
              <v-btn
                v-if="canApprove"
                color="success"
                @click="handleApprove"
                :loading="loadingApprove"
                :disabled="loadingReject || loadingForward"
              >
                Approve
              </v-btn>
              <v-btn
                v-if="canApprove"
                color="blue"
                @click="handleForward"
                :loading="loadingForward"
                :disabled="loadingApprove || loadingReject || loadingReturn"
              >
                Forward
              </v-btn>
              <v-btn
                v-if="canReturn"
                color="warning"
                @click="handleReturn"
                :loading="loadingReturn"
                :disabled="loadingApprove || loadingForward || loadingReject"
              >
                Return
              </v-btn>
              <v-btn
                v-if="canReject"
                color="error"
                @click="handleCancel"
                :loading="loadingReject"
                :disabled="loadingApprove || loadingForward || loadingReturn"
              >
                Reject
              </v-btn>
              <!-- <v-btn
                color="grey"
                @click="goBack"
              >
                Back
              </v-btn> -->
            </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
</template>

<script>
import Axios from "axios";
import { toast } from "vue3-toastify";
import { 
  getApprovalRequestDetails as getApprovalRequestDetailsApi,
  updateApprovalStatus as updateApprovalStatusApi,
  showCmsFile,
  uploadNewVersionCmsFile,
  prefix,
  fileServerUrl
} from "@/store/api";

export default {
  name: "JournalVoucherApprovalPreview",

  data() {
    return {
      approval_process: [],
      documentDetails: null,
      file:null,
      userComment: "",
      commentError: "",
      review_status: null,
      review_comment: "",
      // Separate loading flags so only the clicked action shows loading
      loadingApprove: false,
      loadingReject: false,
      loadingReturn: false,
      loadingForward: false,
      loadingData: true,
      main_file:null,
      selectedReviewerIds: [],
      action_type: null,
      fileServerUrl: fileServerUrl,
      previewDialog: false,
      currentPreviewFile: {
        name: '',
        path: '',
        mime_type: ''
      },
      previewFileType: '',
      confirmDialog: false,
      confirmAction: null,
      confirmMessage: ''
    };
  },

  computed: {
    previousComments() {
      return this.approval_process.filter(
        (approval) => approval.status === 'Approved' || approval.status === 'Cancelled' || approval.status == 'Forwarded' 
      ).filter(approval => approval.comments); // Only show if there are comments
    },

    // Check if user has approve permission using approval_permission from API
    canApprove() {
      const hasPermission = this.currentApprovalStep?.approval_permission === true;
      console.log('Checking canApprove:', {
        currentStep: this.currentApprovalStep,
        approval_permission: this.currentApprovalStep?.approval_permission,
        hasPermission: hasPermission
      });
      return this.currentApprovalStep && hasPermission;
    },

    // Check if user has reject permission using approval_permission from API
    canReject() {
      const hasPermission = this.currentApprovalStep?.approval_permission === true;
      console.log('Checking canReject:', {
        currentStep: this.currentApprovalStep,
        approval_permission: this.currentApprovalStep?.approval_permission,
        hasPermission: hasPermission
      });
      return this.currentApprovalStep && hasPermission;
    },

    canReturn() {
      const hasPermission = this.currentApprovalStep?.approval_permission === true;
      console.log('Checking canReturn:', {
        currentStep: this.currentApprovalStep,
        approval_permission: this.currentApprovalStep?.approval_permission,
        hasPermission: hasPermission
      });
      return this.currentApprovalStep && hasPermission;
    },

    availableReviewers() {
      const step = this.currentApprovalStep;
      const details = step?.document_reviews || [];

      return details.map(detail => ({
        id: detail.id,
        employee_id: detail.employee_id,
        employee_name: detail.employee?.name || '',
        employee_code: detail.employee?.code || '',
        status: detail.status
      }));
    },

    hasReviewers() {
      const step = this.currentApprovalStep;
      const hasReviewFlag =
        step?.has_review === 1 ||
        step?.has_review === '1' ||
        step?.matrix_detail?.has_review === 1 ||
        step?.matrix_detail?.has_review === '1';
      return hasReviewFlag && this.availableReviewers.length > 0;
    },

    reviewerComments() {
      const result = [];

      const step = this.approval_process.find(
        approval =>
          approval.status?.toLowerCase() === 'pending' ||
          approval.status?.toLowerCase() === 'forwarded'
      );

      if (!step || !step.document_reviews) return result;

      step.document_reviews.forEach(review => {
        if (!review.comments) return;

        result.push({
          reviewer_name: review.employee?.name || 'Reviewer',
          reviewer_code: review.employee?.code || '',
          review_status: review.review_status || '',
          comments: review.comments,
          review_date: review.updated_at || review.created_at || null,
        });
      });

      return result;
    },
    currentApprovalStep() {
      // Check if the parent document is already approved (process ended)
      // This happens when is_forward = 1 and Approve was clicked
      const parentApprovalStatus = this.documentDetails?.approval_status;
      if (parentApprovalStatus?.toLowerCase() === 'approved') {
        // console.log('Parent document is already approved - process ended');
        return null; // No current step if already approved
      }

      // Find the first pending approval step
      const currentStep = this.approval_process.find(
        (approval) => (approval.status?.toLowerCase() === 'pending' || approval.status?.toLowerCase() === 'forwarded')
      );
      // currentStep.document_reviews.find(review => {
      //   if(review.status == '1' || review.status == 1){
      //     this.selectedReviewerIds.push(review.employee_id);
      //   }
      // });

      if (!currentStep) return null;

      // The approval_permission is already provided by the API
      // No need to calculate it manually
      // const isForward = currentStep?.matrix_detail?.matrix?.approval_name?.is_forward;
      console.log('Current approval step:', {
        currentUser: this.getAuthUser,
        id: currentStep.id,
        employee: currentStep.employee?.name,
        approval_step_name: currentStep.approval_step_name?.name,
        approval_permission: currentStep.approval_permission,
        status: currentStep.status,
        // is_forward: isForward,
        is_final_step: currentStep.is_final_step,
        matrix_detail: currentStep.matrix_detail,
        parent_approval_status: parentApprovalStatus
      });
      
      return currentStep;
    },

    currentReviewStep() {
      const currentStep = this.currentApprovalStep;
      console.log('currentStep:', currentStep);
      console.log('getAuthUser:', this.getAuthUser);
      console.log('employee id:', this.getAuthUser?.employee?.id);
      if (!currentStep || !currentStep.document_reviews) return null;
      const currentReview = currentStep.document_reviews.find(
        (review) => {
          console.log('review employee_id:', review.employee_id, 'type:', typeof review.employee_id);
          return String(review.employee_id) === String(this.getAuthUser?.employee?.id);
        }
      );
      console.log('currentReview:', currentReview);
      return currentReview || null;
    },

    isFinalStep() {
      // Use the is_final_step field directly from the API response
      return this.currentApprovalStep?.is_final_step === 1;
    },

    // Check if forward is enabled for this approval name
    isForwardEnabled() {
      // Check is_forward from the nested structure: matrix_detail.matrix.approval_name.is_forward
      const isForward = this.currentApprovalStep?.matrix_detail?.matrix?.approval_name?.is_forward;
      const enabled = isForward === 1 || isForward === '1';
      console.log('isForwardEnabled check:', {
        isForward: isForward,
        enabled: enabled,
        currentStep: this.currentApprovalStep?.id,
        approval_name: this.currentApprovalStep?.matrix_detail?.matrix?.approval_name
      });
      return enabled;
    },

    // Check if the approval process is complete
    isProcessComplete() {
      // Check from documentDetails first
      let parentApprovalStatus = this.documentDetails?.approval_status;
      
      // If not found, try to get it from the first approval step's referenceable
      if (!parentApprovalStatus && this.approval_process.length > 0) {
        parentApprovalStatus = this.approval_process[0]?.referenceable?.approval_status;
      }
      
      const isComplete = parentApprovalStatus?.toLowerCase() === 'approved';
      console.log('isProcessComplete check:', {
        documentDetails: this.documentDetails,
        approval_status: parentApprovalStatus,
        isComplete: isComplete,
        firstStepReferenceable: this.approval_process[0]?.referenceable
      });
      return isComplete;
    },

    // Check if the process was completed via forward approval (is_forward = 1)
    wasForwardApproved() {
      if (!this.isProcessComplete) return false;
      
      // Check if any approved step has is_forward = 1
      const approvedStep = this.approval_process.find(
        (approval) => approval.status?.toLowerCase() === 'approved'
      );
      
      if (approvedStep) {
        const isForward = approvedStep?.matrix_detail?.matrix?.approval_name?.is_forward;
        return isForward === 1 || isForward === '1';
      }
      
      return false;
    },
    getAuthUser() {
      return this.$store.getters.getAuthUser;
    },
  },

  watch: {
    currentApprovalStep: {
      immediate: true,
      handler(step) {
        if (!step || !step.document_reviews) {
          this.selectedReviewerIds = [];
          return;
        }

        this.selectedReviewerIds = step.document_reviews
          .filter(r => r.status == 1 || r.status == '1')
          .map(r => r.employee_id);
      }
    },
    userComment() {
      if (this.commentError && this.userComment) {
        this.commentError = '';
      }
    }
  },

  created() {
    console.log('Approval Preview Component Created');
    console.log('Voucher ID from route:', this.$route.params.id);
    this.loadApprovalDetails();
  },

  methods: {
   
    async loadApprovalDetails() {
      try {
        this.loadingData = true;
        
        if (!this.$route?.params?.id && !this.$route?.query?.referenceable_id) {
          toast.error("No reference ID provided");
          this.goBack();
          return;
        }

        // Get parameters from route query
        const referenceableType = this.$route?.query?.referenceable_type;
        const referenceableId = this.$route?.query?.referenceable_id || this.$route.params.id;
        
        // Check if referenceable_type is provided
        if (!referenceableType) {
          toast.error("Missing reference type. Unable to load approval details.");
          this.goBack();
          return;
        }
        
        console.log('Loading approval details...');
        console.log('Referenceable Type:', referenceableType);
        console.log('Referenceable ID:', referenceableId);
        
        const payload = {
          referenceable_type: referenceableType,
          referenceable_id: referenceableId,
        };
        
        const response = await Axios.post(getApprovalRequestDetailsApi, payload);
        if(referenceableType == 'Modules\\CMS\\Models\\CmsDocumentFile'){
          const api = showCmsFile(referenceableId)
          const fileRes = await Axios.get(api);
          this.file = fileRes.data.data;
        }

        if (response.data.success) {
          // console.log('API Response:', response.data);
          this.approval_process = response.data.data || [];
          // console.log('Current approval process:', this.approval_process);
          
          // Log the current step if it exists
          const currentStep = this.approval_process.find(step => step.status?.toLowerCase() === 'pending');
          console.log('Current step details:', currentStep);
          if (this.approval_process.length > 0) {
            this.documentDetails = this.approval_process[0].referenceable;
            
            // Log the parent approval status for debugging
            console.log('Parent document approval_status:', this.documentDetails?.approval_status);
            
            // If parent is already approved, log it
            if (this.documentDetails?.approval_status?.toLowerCase() === 'approved') {
              console.log('Process is complete - parent document is approved');
            }
          } else {
            toast.warning("No approval steps found for this document");
          }
        } else {
          toast.error(response.data.message || "Failed to load approval details");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load approval details");
        console.error('Error loading approval details:', error);
        this.goBack();
      } finally {
        this.loadingData = false;
      }
    },

    async saveReview() {
      if (!this.review_status) {
        toast.error("Please select a review status");
        return;
      }

       const response = await Axios.post(
          `${prefix}administration/review-status/update/${this.currentReviewStep.id}`,
          {  review_status: this.review_status, review_comment: this.review_comment },
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          // Reset review fields
          this.review_status = null;
          this.review_comment = '';
          toast.success('Comment successfully added');
          // Clear only the comment text after successful forward
          this.userComment = '';
          this.commentError = '';
          setTimeout(() => {
            this.$router.push({ name: "approval-request-record", query: {page_title: 'CMS Document Request', model: 'CmsDocumentFile'} });
          }, 1000);

        }
    },

    async handleForward() {
      if (!this.canApprove) {
        toast.error("You are not authorized to forward this step");
        return;
      }

      this.confirmAction = 'forward';
      this.confirmMessage = 'Are you sure you want to forward this request?';
      this.confirmDialog = true;
    },

    async handleApprove() {
      if (!this.canApprove) {
        toast.error("You are not authorized to approve this step");
        return;
      }

      this.confirmAction = 'approve';
      this.confirmMessage = 'Are you sure you want to approve this request?';
      this.confirmDialog = true;
    },

    async handleCancel() {
      if (!this.canReject) {
        toast.error("You are not authorized to cancel this step");
        return;
      }

      this.confirmAction = 'cancel';
      this.confirmMessage = 'Are you sure you want to reject this request?';
      this.confirmDialog = true;
    },

    async handleReturn() {
      if (!this.canReturn) {
        toast.error("You are not authorized to Return this step");
        return;
      }

      this.confirmAction = 'return';
      this.confirmMessage = 'Are you sure you want to return this request?';
      this.confirmDialog = true;
    },

    getDateField() {
      if (!this.documentDetails) return 'N/A';
      
      // Try common date field names
      return this.documentDetails.voucher_date || 
             this.documentDetails.requisition_date || 
             this.documentDetails.order_date || 
             this.documentDetails.date || 
             this.documentDetails.created_at || 
             'N/A';
    },

    getAmountField() {
      if (!this.documentDetails) return 'N/A';
      
      // Try common amount field names
      const amount = this.documentDetails.total_debit ?? 
                     this.documentDetails.total_amount ?? 
                     this.documentDetails.amount ?? 
                     this.documentDetails.total_credit ??
                     this.documentDetails.grand_total ??
                     this.documentDetails.net_amount ??
                     this.documentDetails.total;
      
      return amount !== null && amount !== undefined ? this.formatCurrency(amount) : 'N/A';
    },

    goBack() {
      this.$router.push({ name: "approval-request-record" });
    },

    // getCurrentEmployee() {
    //   return this.$store.state.auth.user.employee || {};
    // },

    isCurrentStep(index) {
      // Don't show current step if process is complete
      if (this.isProcessComplete) return false;
      
      if (!this.approval_process || this.approval_process.length === 0) return false;

      const firstPendingIndex = this.approval_process.findIndex(
        (approval) => approval.status?.toLowerCase() === 'pending'
      );

      return index === firstPendingIndex;
    },

    getCurrentStepStartTime(index) {
      // If there's a previous step and it's approved, use its approval time
      if (index > 0 && this.approval_process[index - 1]) {
        const previousStep = this.approval_process[index - 1];
        if (previousStep.status?.toLowerCase() === 'approved' && previousStep.approved_at) {
          return previousStep.approved_at;
        }
      }
      // Otherwise, use the current step's created_at
      return this.approval_process[index]?.created_at;
    },

    getApprovalClass(status, index) {
      if (!status) return 'step-circle-grey';
      const statusLower = status.toLowerCase();
      if (statusLower === 'approved') return 'step-circle-success';
      if (statusLower === 'forwarded') return 'step-circle-success';
      if (statusLower === 'pending') {
        // If process is complete and step is pending, show as skipped
        if (this.isProcessComplete) return 'step-circle-skipped';
        return this.isCurrentStep(index) ? 'step-circle-blue' : 'step-circle-pending';
      }
      if (statusLower === 'returned') return 'step-circle-warning';
      if (statusLower === 'rejected' || statusLower === 'cancelled') return 'step-circle-error';
      return 'step-circle-grey';
    },

    getApprovalIcon(status, index) {
      if (!status) return 'mdi-help';
      const statusLower = status.toLowerCase();
      if (statusLower === 'approved') return 'mdi-check';
      if (statusLower === 'forwarded') return 'mdi-skip-next';
      if (statusLower === 'returned') return 'mdi-undo';
      if (statusLower === 'pending') {
        // If process is complete and step is pending, show as skipped
        if (this.isProcessComplete) return 'mdi-skip-next';
        return this.isCurrentStep(index) ? 'mdi-reload' : 'mdi-clock-outline';
      }
      if (statusLower === 'returned') return 'mdi-undo';
      if (statusLower === 'rejected' || statusLower === 'cancelled') return 'mdi-close';
      return 'mdi-help';
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },

    getFileIcon(file){
      const ext = file?.stored_name?.split(".").pop().toLowerCase();

      switch (ext) {
        case "pdf":
          return "mdi mdi-file-pdf-box";
        case "doc":
        case "docx":
          return "mdi mdi-file-word";
        case "xls":
        case "xlsx":
        case "csv":
          return "mdi mdi-microsoft-excel";
        case "jpg":
        case "jpeg":
        case "png":
          return "mdi mdi-image-album";
        case "ppt":
        case "pptx":
          return "mdi mdi-file-powerpoint";
        case "zip":
        case "rar":
        case "7z":
          return "mdi mdi-folder-zip";
        case "txt":
          return "mdi mdi-file-document-outline";
        default:
          return "mid mdi-file";
      }
    },

    getFileColor(file){
      const ext = file?.stored_name?.split(".").pop().toLowerCase();
      switch (ext) {
        case "pdf":
          return "red";
        case "doc":
        case "docx":
          return "blue";
        case "xls":
        case "xlsx":
        case "csv":
          return "green";
        case "jpg":
        case "jpeg":
        case "png":
          return "purple";
        case "ppt":
        case "pptx":
          return "orange";
        case "zip":
        case "rar":
        case "7z":
          return "brown";
        case "txt":
          return "grey";
        default:
          return "grey";
      }
    },

    async downloadFile(file, type) {
      try {
        const blob = await this.$store.dispatch("downlosdCmsFile", {
            fileId: file.id,
            type: type, 
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = file.original_name;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(link.href); // ✅ memory clean

      } catch (error) {
        console.error("Download failed", error);
        alert("File download failed");
      }
    },

    async uploadNewVersion() {
      if (!this.main_file) {
        toast.error("Please select a file");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("file", this.main_file);
        formData.append("file_id", this.file.id);

        this.$store.commit("SET_LOADING", { color: "primary" });
        const res = await Axios.post( uploadNewVersionCmsFile, formData, { headers: { "Content-Type": "multipart/form-data" } })
        .then((response) => {
          this.$store.commit("UNSET_LOADING");
          toast.success("New version uploaded successfully");
          this.main_file = null;
          this.loadApprovalDetails();
        })
        .catch((error) => {
          this.$store.commit("UNSET_LOADING");
          throw error;
        });
      } catch (e) {
        toast.error("File upload failed");
        console.error(e);
      }
    },

    triggerNewVersionInput() {
      if (this.$refs.newVersionInput) {
        this.$refs.newVersionInput.click();
      }
    },

    onNewVersionFileChange(event) {
      const files = event.target.files;
      this.main_file = files && files[0] ? files[0] : null;
      if (this.main_file) {
        this.uploadNewVersion();
      }
    },

    isImage(mimeType) {
      return mimeType && mimeType.startsWith('image/');
    },

    isPdf(mimeType) {
      return mimeType === 'application/pdf';
    },

    previewFile(file, type) {
      this.currentPreviewFile = {
        name: file.original_name,
        path: file.path,
        mime_type: file.mime_type
      };
      this.previewFileType = type;
      this.previewDialog = true;
    },

    async executeConfirmedAction() {
      this.confirmDialog = false;

      switch (this.confirmAction) {
        case 'approve':
          await this.processApproval('Approved');
          break;
        case 'forward':
          await this.processApproval('Forwarded');
          break;
        case 'return':
          await this.processApproval('Returned');
          break;
        case 'cancel':
          await this.processApproval('Cancelled');
          break;
      }
    },

    async processApproval(status) {
      try {
        const formData = new FormData();
        formData.append('comments', this.userComment || '');
        formData.append('status', status);

        if (this.hasReviewers && this.selectedReviewerIds.length) {
          this.selectedReviewerIds.forEach(id => {
            formData.append('reviewer_ids[]', id);
          });
        }

        const response = await Axios.post(
          `${updateApprovalStatusApi}/${this.currentApprovalStep.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          toast.success(`${status} successfully`);
          this.userComment = '';
          this.commentError = '';

          setTimeout(() => {
            this.$router.push({
              name: "approval-request-record",
              query: {
                page_title: 'CMS Document Request',
                model: 'CmsDocumentFile'
              }
            });
          }, 1000);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to ${status}`);
      }
    }


  },
  mounted() {
    this.action_type = this.$route?.query?.action_type;
  }
};
</script>

<style scoped>
/* Timeline Styles */
.horizontal-timeline {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  padding: 20px 15px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.timeline-step {
  display: flex;
  align-items: flex-start;
  position: relative;
  min-width: 180px;
  flex-shrink: 0;
}

.step-connector {
  width: 60px;
  height: 2px;
  background: #e0e0e0;
  position: absolute;
  top: 24px;
  left: -30px;
  z-index: 0;
  transition: all 0.3s ease;
}

.connector-approved {
  background: #4CAF50;
}
.connector-returned {
  background: #fb8c00;
}

.connector-arrow {
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid #e0e0e0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.connector-approved .connector-arrow {
  border-left-color: #4CAF50;
}
.connector-returned .connector-arrow {
  border-left-color: #4CAF50;
}

.step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.step-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
  position: relative;
  border: 2px solid white;
}

.step-circle-success {
  background: linear-gradient(135deg, #4CAF50 0%, #2e7d32 100%);
}

.step-circle-warning {
  background: linear-gradient(135deg, #f7ae56 0%, #fb8c00 100%);
}



.step-circle-pending {
  background: linear-gradient(135deg, #FF9800 0%, #ef6c00 100%);
}

.step-circle-blue {
  background: linear-gradient(135deg, #2196F3 0%, #1565C0 100%);
  animation: pulse-ring 2s infinite;
}

.step-circle-error {
  background: linear-gradient(135deg, #f44336 0%, #c62828 100%);
}

.step-circle-grey {
  background: linear-gradient(135deg, #9e9e9e 0%, #616161 100%);
}

.step-circle-skipped {
  background: linear-gradient(135deg, #9e9e9e 0%, #757575 100%);
  opacity: 0.6;
}

@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7), 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0), 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0), 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.step-info {
  text-align: center;
}

.step-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 2px;
}

.status-approved-text {
  color: #4caf50 !important;
}

.status-pending-text {
  color: #ef6c00 !important;
}

.status-cancelled-text {
  color: #f44336 !important;
}

.status-skipped-text {
  color: #757575 !important;
  font-style: italic;
}

.step-status {
  font-size: 0.75rem;
  color: #666;
}

.employee-name {
  font-size: 0.75rem;
  color: #424242;
  font-weight: 600;
  margin-bottom: 2px;
}

.current-step-title {
  color: #2196F3 !important;
}

/* Comment Box Styles */
.comment-box {
  background: #f5f5f5;
  border-left: 3px solid #2196F3;
  padding: 8px 12px;
  border-radius: 4px;
  word-break: break-word;
  transition: all 0.3s ease;
}

.comment-box-rejected {
  background: #fff5f5;
  border-left-color: #f44336;
}

.comment-box-approved {
  background: #f5fff5;
  border-left-color: #4caf50;
}

.comment-header {
  font-weight: 600;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 4px;
  line-height: 1.4;
}

.comment-status {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.status-rejected {
  background-color: #ffebee;
  color: #d32f2f;
}

.status-approved {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.comment-date {
  font-size: 0.75rem;
  color: #666;
  margin-top: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>

